import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CalendarCheck, 
  ArrowLeft,
  UserPlus,
  FileText,
  Calendar,
  BellRing,
  CreditCard,
  Settings,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Smartphone,
  Mail,
  Shield,
  HelpCircle,
  BookOpen,
  PlayCircle
} from 'lucide-react';

const ResourcesPage = () => {
  const gettingStartedSteps = [
    {
      step: 1,
      icon: <UserPlus className="w-6 h-6" />,
      title: "Create your account",
      description: "Sign up free with email or Google. No credit card, no commitment.",
      link: "/register"
    },
    {
      step: 2,
      icon: <FileText className="w-6 h-6" />,
      title: "Add your first renewal",
      description: "Tap 'Add Renewal' and fill in the title, category, due date, and how you'd like to be reminded.",
      link: "/renewals"
    },
    {
      step: 3,
      icon: <BellRing className="w-6 h-6" />,
      title: "Set up notifications",
      description: "Pick your channels — email, SMS, or both — and decide how early you want the heads-up.",
      link: "/settings"
    },
    {
      step: 4,
      icon: <CheckCircle className="w-6 h-6" />,
      title: "Sit back, stay ahead",
      description: "Check the dashboard or calendar whenever. We'll handle the reminding.",
      link: "/dashboard"
    }
  ];

  const features = [
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Managing Renewals",
      description: "Add, edit, and organize all your renewal reminders in one place.",
      details: [
        "Create renewals for documents, subscriptions, and insurance",
        "Set custom reminder schedules (1, 3, 7, 14, or 30 days before)",
        "Add notes and descriptions for each renewal",
        "Filter and search through your renewals easily"
      ]
    },
    {
      icon: <RefreshCw className="w-5 h-5" />,
      title: "Recurring Renewals",
      description: "Automatically create the next renewal when you mark one as complete.",
      details: [
        "Enable 'Recurring Renewal' when creating a reminder",
        "Set recurrence period (1 month to 10 years)",
        "When marked as renewed, next reminder is auto-created",
        "Perfect for passports, licenses, and annual subscriptions"
      ]
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      title: "Calendar View",
      description: "Visualize all your renewals on an intuitive calendar interface.",
      details: [
        "See all renewals for any month at a glance",
        "Click any date to view renewals due that day",
        "Dots indicate days with upcoming renewals",
        "Plan ahead with the monthly overview sidebar"
      ]
    },
    {
      icon: <BellRing className="w-5 h-5" />,
      title: "Notifications",
      description: "Get timely reminders via email and SMS before your deadlines.",
      details: [
        "Enable email notifications for all plans",
        "SMS notifications available on Pro plans and above",
        "Customize which days before renewal you get reminded",
        "Test notifications from the Settings page"
      ]
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: "Subscription Plans",
      description: "Choose the plan that fits your needs.",
      details: [
        "Free: Track up to 3 renewals with email notifications",
        "Basic ($4.99/mo): Up to 10 renewals",
        "Pro ($9.99/mo): Up to 50 renewals + SMS notifications",
        "Enterprise ($19.99/mo): Unlimited renewals + priority support"
      ]
    },
    {
      icon: <Settings className="w-5 h-5" />,
      title: "Account Settings",
      description: "Manage your profile and notification preferences.",
      details: [
        "Update your email and phone number for notifications",
        "Toggle email and SMS notifications on/off",
        "Send test notifications to verify setup",
        "View your current subscription plan"
      ]
    }
  ];

  const faqs = [
    {
      question: "What types of renewals can I track?",
      answer: "You can track three categories: Documents (passports, licenses, visas, IDs), Subscriptions (software, memberships, streaming services), and Insurance (health, auto, home, life policies)."
    },
    {
      question: "How do reminder notifications work?",
      answer: "When you create a renewal, you select how many days before the deadline you want to be reminded (e.g., 7 days, 3 days, 1 day before). You'll receive notifications via email and/or SMS based on your preferences."
    },
    {
      question: "What happens when I mark a renewal as complete?",
      answer: "If you have 'Recurring Renewal' enabled, a new renewal will automatically be created for the next period (e.g., 1 year later for a passport). If not recurring, the renewal is simply marked as 'Renewed' in your history."
    },
    {
      question: "Can I use Enota on my phone?",
      answer: "Yes! Enota is fully responsive and works great on mobile browsers. You can add, edit, and view your renewals from any device."
    },
    {
      question: "How do I upgrade my plan?",
      answer: "Go to the Billing page from your dashboard sidebar. Select the plan you want and complete the secure checkout process via Stripe."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use industry-standard encryption for all data in transit and at rest. We never sell your personal information. Read our Privacy Policy for full details."
    }
  ];

  return (
    <div className="min-h-screen bg-paper">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-ink-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <CalendarCheck className="w-7 h-7 text-ink" />
              <span className="text-xl font-bold text-ink font-display">Enota</span>
            </Link>
            <Link to="/" className="text-ink-600 hover:text-ink flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" />
              Back to home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-12 md:py-16 bg-white border-b border-ink-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 rounded-full bg-cream flex items-center justify-center mx-auto mb-6">
            <BookOpen className="w-7 h-7 text-respondent" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-ink tracking-tight font-display mb-4">
            Help &amp; how-to.
          </h1>
          <p className="text-lg text-ink-600 max-w-2xl mx-auto">
            Quick guides, feature walk-throughs, and answers to the questions we hear most. Bookmark it.
          </p>
        </div>
      </section>

      {/* Getting Started */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-respondent uppercase tracking-[0.2em] mb-3">Quick start</p>
            <h2 className="text-3xl font-bold text-ink tracking-tight font-display">
              Up and running in four steps.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {gettingStartedSteps.map((item) => (
              <Card key={item.step} className="bg-white border border-ink-200 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 left-0 w-10 h-10 bg-ink text-white flex items-center justify-center font-bold text-lg rounded-br-lg">
                  {item.step}
                </div>
                <CardContent className="p-6 pt-12">
                  <div className="w-12 h-12 rounded-md bg-cream flex items-center justify-center text-respondent mb-4">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-ink mb-2 font-display">{item.title}</h3>
                  <p className="text-ink-600 text-sm mb-4">{item.description}</p>
                  <Link to={item.link}>
                    <Button variant="ghost" size="sm" className="text-respondent hover:text-respondent hover:bg-cream p-0">
                      Learn more <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Guide */}
      <section className="py-12 md:py-16 bg-white border-y border-ink-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-respondent uppercase tracking-[0.2em] mb-3">Features</p>
            <h2 className="text-3xl font-bold text-ink tracking-tight font-display">
              How to Use Each Feature
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-paper border border-ink-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-md bg-cream flex items-center justify-center text-respondent">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-ink font-display">{feature.title}</h3>
                  </div>
                  <p className="text-ink-600 text-sm mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-ink-600">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Tutorial Placeholder */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-br from-ink to-ink border-0 shadow-lg overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <PlayCircle className="w-10 h-10 text-white" />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white font-display mb-2">
                    Watch the Quick Start Video
                  </h3>
                  <p className="text-paper mb-4">
                    Learn how to set up your first renewal in under 2 minutes with our step-by-step video guide.
                  </p>
                  <Button className="bg-white text-ink hover:bg-cream">
                    Coming Soon
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 md:py-16 bg-white border-t border-ink-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-respondent uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="text-3xl font-bold text-ink tracking-tight font-display">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-paper border border-ink-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-cream flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-4 h-4 text-respondent" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-ink mb-2">{faq.question}</h3>
                      <p className="text-ink-600 text-sm leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 bg-paper">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-ink font-display mb-4">
            Still have questions?
          </h2>
          <p className="text-ink-600 mb-6">
            We answer email like humans, not bots. Hit us up — we'll get back within a business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-ink hover:bg-black text-white px-6">
                Contact support
              </Button>
            </Link>
            <Link to="/register">
              <Button variant="outline" className="border-ink-200 hover:bg-cream hover:text-ink px-6">
                Get started — free
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-ink text-ink-600/70 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <CalendarCheck className="w-6 h-6 text-white" />
              <span className="text-lg font-bold text-white font-display">Enota</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/resources" className="hover:text-white transition-colors">Resources</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/california-privacy" className="hover:text-white transition-colors">California Residents</Link>
            </div>
            <p className="text-sm">&copy; 2026 Enota. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesPage;
